import RestClient from "sh-rest-client";
import { DataProvider } from "sh-services/dataproviders/DataProvider";
import { ITeamDatabase } from "sh-services/data/ITeamDatabase";
import { IUserTeamSettingsEntity } from "sh-models";
import { setUserTeamSettingsForTeam, UserTeamSettingsStore } from "sh-stores";

/**
 * UserTeamSettings Data Provider
 *
 * Implements three-tier caching (Memory → IndexedDB → Network) for user-specific team configuration.
 * Instantiated per-request with teamId context. User context from auth token (not in URL).
 */
export class UserTeamSettingsDataProvider extends DataProvider<IUserTeamSettingsEntity> {
    protected teamDatabase: ITeamDatabase; // IndexedDB instance for this team
    protected teamId: string; // Team these settings belong to

    constructor(teamDatabase: ITeamDatabase, teamId: string) {
        super();
        this.teamDatabase = teamDatabase;
        this.teamId = teamId;
    }

    /**
     * Get from memory (MobX store). Called first on every read.
     */
    async getDataFromMemory(): Promise<IUserTeamSettingsEntity | undefined> {
        const store = UserTeamSettingsStore();
        return store.userTeamSettingsByTeam.get(this.teamId);
    }

    /**
     * Get from IndexedDB. Called if memory miss. Triggers background refresh.
     */
    async getDataFromDatabase(): Promise<IUserTeamSettingsEntity | undefined> {
        return await this.teamDatabase.getUserTeamSettings(this.teamId);
    }

    /**
     * Get from network (REST API). Returns null if doesn't exist (lazy creation).
     */
    async getDataFromNetwork(): Promise<IUserTeamSettingsEntity> {
        return await RestClient.getUserTeamSettings(this.teamId);
    }

    /**
     * Update memory (MobX store). Triggers immediate UI update.
     */
    async setDataInMemory(data: IUserTeamSettingsEntity): Promise<void> {
        setUserTeamSettingsForTeam(this.teamId, data);
    }

    /**
     * Update IndexedDB for offline persistence.
     */
    async setDataInDatabase(data: IUserTeamSettingsEntity): Promise<void> {
        return await this.teamDatabase.setUserTeamSettings(this.teamId, data);
    }

    /**
     * Save to network (REST API PUT). Returns entity with new eTag.
     * Requires eTag for optimistic concurrency control (409 if mismatch).
     */
    async saveDataToNetwork(data: IUserTeamSettingsEntity): Promise<IUserTeamSettingsEntity> {
        return await RestClient.updateUserTeamSettings(this.teamId, data);
    }
}
